import { EmptyRequestT } from 'types/shared/reqAndRes/CommonRequest';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import { MyInfoT } from 'types/shared/core/User';
import { READ_MY_INFO } from 'network/shared/user';
import { api } from 'utils/api/api';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { StudentT } from 'types/shared/core/Student';
import { AcademySettingT } from 'types/shared/core/Academy';
import { getActiveAcademyId } from './RootStore';
import { pagesWithoutMyInfo } from './pagesWithoutMyInfo';
import InappVal from 'constants/frontend/inapp/InappVal';
import Token from 'utils/Token';
import { handleDates } from 'utils/api/dateHandler';

const MY_INFO_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const MY_INFO_CACHE_VERSION = 1;

type MyInfoCacheEnvelope = {
  v: number;
  ts: number; // saved at (ms)
  userId: number;
  payload: MyInfoT;
};

function saveCache(userId: number, myInfo: MyInfoT) {
  try {
    const env: MyInfoCacheEnvelope = {
      v: MY_INFO_CACHE_VERSION,
      ts: Date.now(),
      userId,
      payload: myInfo,
    };
    localStorage.setItem(InappVal.LK_MY_INFO_CACHE, JSON.stringify(env));
  } catch {}
}

function loadCache(): MyInfoT | null {
  const currentUserId = Token.getCurrentUserId();
  if (!currentUserId) return null;

  try {
    const raw = localStorage.getItem(InappVal.LK_MY_INFO_CACHE);
    if (!raw) return null;

    const env = JSON.parse(raw) as MyInfoCacheEnvelope;
    if (env.v !== MY_INFO_CACHE_VERSION) return null;
    if (Date.now() - env.ts > MY_INFO_CACHE_TTL_MS) return null;
    if (env.userId !== currentUserId) return null;
    if (env.payload?.userInfo.id !== currentUserId) return null;

    handleDates(env.payload);
    return env.payload;
  } catch {
    return null;
  }
}

function clearCache() {
  try {
    localStorage.removeItem(InappVal.LK_MY_INFO_CACHE);
  } catch {}
}

export function isDevelopmentEnv() {
  const env = location.host.includes('-local') ? 'development' : 'prod';
  return env === 'development';
}

export function isProd() {
  return !isDevelopmentEnv();
}

class UserStore {
  myInfo: MyInfoT | null = null;
  myInfoVersion = '';
  myInfoTimestamp = '';

  constructor() {
    makeAutoObservable(this);

    const cached = loadCache();
    if (cached) {
      this.myInfo = cached;
    }

    // 생성과 함께 언제나 실행
    this.loadMyInfo();

    reaction(
      () => this.myInfoVersion,
      () => this.loadMyInfo(),
    );
  }

  setVersion = (v: string) => {
    this.myInfoVersion = v;
  };

  // 자신의 정보 불러오기
  loadMyInfo = async () => {
    if (pagesWithoutMyInfo.includes(window.location.pathname)) {
      console.log('this page does not need MyInfo');
      return;
    }

    console.log('MY_INFO loading...');

    // 촬영용 dev 환경: 인위적인 응답 지연을 잠시 비활성화합니다.
    // if (isDevelopmentEnv()) {
    //   const n = 1000;
    //   console.log(`In development mode, waiting for ${n}ms`);
    //   await new Promise((resolve) => setTimeout(resolve, n));
    // }

    try {
      const response = await api<EmptyRequestT, MyResponse<MyInfoT>>(
        READ_MY_INFO,
        {},
      );

      runInAction(() => {
        if (response.isSuccess && response.object) {
          this.myInfo = response.object as MyInfoT;
          this.myInfoTimestamp = new Date().toString();
          saveCache(this.myInfo.userInfo.id, response.object);
          console.log('MY_INFO success', this.myInfo);
        } else {
          // 나의 정보 읽어오지 못함
          console.log('MY_INFO error');
          clearCache();
        }
      });
    } catch (e) {
      // 오류
      console.log('MY_INFO exception', e);
    }
  };

  // 자신과 연결된 학생 정보 얻기
  getStudent = (studentId: number): StudentT | null => {
    if (!this.myInfo) return null;
    if (this.myInfo.userInfo.role !== 'parent') return null;
    if (!this.myInfo.parentInfo?.linkedAcademyList) return null;
    const studentList = this.myInfo.parentInfo.linkedAcademyList
      .map((la) => la.linkedStudents)
      .flat()
      .filter((s) => s.id === studentId);
    if (studentList.length !== 1) return null;
    else return studentList[0];
  };

  get isDirector(): boolean {
    return this.myInfo?.teacherInfo?.teacher.position === 'director';
  }

  get isManager(): boolean {
    return (
      this.myInfo?.teacherInfo?.teacher.position === 'director' ||
      this.myInfo?.teacherInfo?.teacher.position === 'assistantDirector'
    );
  }

  get isAssistantDirector(): boolean {
    return this.myInfo?.teacherInfo?.teacher.position === 'assistantDirector';
  }

  // 운영자&학원비 담당 또는 원장
  get isFinancialManager(): boolean {
    return (
      (this.myInfo?.teacherInfo?.teacher.position === 'assistantDirector' &&
        this.myInfo?.teacherInfo?.teacher.isFinancialManager) ||
      this.myInfo?.teacherInfo?.teacher.position === 'director'
    );
  }

  get isTeacher(): boolean {
    return this.myInfo?.userInfo.role === 'teacher';
  }

  get isParent(): boolean {
    return this.myInfo?.userInfo.role === 'parent';
  }

  get isStudent(): boolean {
    return this.myInfo?.userInfo.role === 'student';
  }

  get activeAcademySetting(): AcademySettingT | null {
    const activeAcademyId = getActiveAcademyId();
    if (!activeAcademyId) return null;

    if (this.isTeacher) {
      const academy = this.myInfo?.teacherInfo?.academy;
      if (!academy) return null;

      return {
        insiderSetting: academy.insiderSetting,
        publicSetting: academy.publicSetting,
      };
    } else if (this.isParent) {
      const filteredAcademyList =
        this.myInfo?.parentInfo?.linkedAcademyList.filter(
          (academy) => academy.academy.id === activeAcademyId,
        );
      if (!filteredAcademyList?.length) return null;
      const academy = filteredAcademyList[0].academy;
      return {
        publicSetting: academy.publicSetting,
      };
    } else if (this.isStudent) {
      const filteredAcademyList =
        this.myInfo?.studentInfo?.linkedAcademyList.filter(
          (academy) => academy.academy.id === activeAcademyId,
        );
      if (!filteredAcademyList?.length) return null;

      const academy = filteredAcademyList[0].academy;
      return {
        publicSetting: academy.publicSetting,
      };
    }
    return null;
  }
}

export default UserStore;
